import { readonly, ref } from 'vue'
import {
  FAIR_TOAST_INTERACTIVE_LIFE_MS,
  FAIR_TOAST_LIFE_MS,
} from '@/ui/tokens/fair-toast.data'

export type FairToastTone = 'success' | 'warning'

export interface FairToastOptions {
  /** Plain message, or text before an optional underlined name/action. */
  message: string
  /** Optional inline underlined label (list name, Try again, Open, Undo). */
  linkLabel?: string
  onLinkClick?: () => void
  /** Auto-dismiss ms; default FAIR_TOAST_LIFE_MS (3000). */
  life?: number
  /** Leading icon; default true. */
  showIcon?: boolean
  /** `success` = check · `warning` = error/info circle. */
  tone?: FairToastTone
}

export interface FairToastItem {
  id: number
  message: string
  linkLabel?: string
  onLinkClick?: () => void
  showIcon: boolean
  tone: FairToastTone
  life: number
}

const toasts = ref<FairToastItem[]>([])
let nextId = 1
const timers = new Map<number, ReturnType<typeof setTimeout>>()

function clearTimer(id: number) {
  const timer = timers.get(id)
  if (timer != null) {
    clearTimeout(timer)
    timers.delete(id)
  }
}

function dismiss(id: number) {
  clearTimer(id)
  toasts.value = toasts.value.filter((item) => item.id !== id)
}

function clearAll() {
  for (const id of timers.keys()) {
    clearTimer(id)
  }
  toasts.value = []
}

function show(options: FairToastOptions) {
  const rawMessage = options.message ?? ''
  const message = rawMessage.trimStart()
  if (!message.trim()) return

  clearAll()

  const id = nextId++
  const linkLabel = options.linkLabel?.trim() || undefined
  const life =
    options.life ??
    (linkLabel ? FAIR_TOAST_INTERACTIVE_LIFE_MS : FAIR_TOAST_LIFE_MS)
  const displayMessage = linkLabel
    ? (message.endsWith(' ') ? message : `${message.trimEnd()} `)
    : message.trim()

  const item: FairToastItem = {
    id,
    message: displayMessage,
    linkLabel,
    onLinkClick: options.onLinkClick,
    showIcon: options.showIcon !== false,
    tone: options.tone ?? 'success',
    life,
  }

  toasts.value = [item]

  if (life > 0) {
    timers.set(
      id,
      setTimeout(() => {
        dismiss(id)
      }, life),
    )
  }
}

/** Module singleton — host + callers share one queue. */
export function useFairToast() {
  return {
    toasts: readonly(toasts),
    show,
    dismiss,
    clearAll,
  }
}
